import { useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useCreateComponentMutation } from '../api/authoring'
import type { Document, Manifest } from '../api/authoring'
import { problemMessage } from '../api/baseApi'
import { typeOf } from '../model/manifest'
import { vocabularyAt } from '../model/definition'
import { shown } from '../api/useIntent'

/**
 * Declaring a component type.
 *
 * **A definition is created whole, and it has to be.** Every revision of
 * a definition is validated as a definition, and one with no fields is a
 * type nothing could hold anything in while one with no intents is a type
 * nothing could ever edit — so the platform refuses both, and the first
 * revision is no exception. This dialog therefore asks for the smallest
 * declaration that is actually a type, and the designer edits everything
 * afterwards.
 *
 * The starter is a required text field and one intent that writes it,
 * which is not a template so much as the minimum the rules describe. It
 * is named after what the author typed rather than left blank, because
 * "title" is what nine records in ten call the thing that names them and
 * an author who wanted otherwise renames it in the designer.
 *
 * It is a dialog of its own rather than a branch inside
 * `CreateComponentDialog` for the reason `TypeEditor` is its own editor:
 * a declaration is two lists of records, the derived create form draws
 * scalars, and a form that quietly omitted `fields` and `intents` would
 * send a creation the surface refuses with an error about a shape the
 * author was never shown.
 */
export function NewTypeDialog({
  tenant,
  metaType,
  open,
  manifest,
  onClose,
  onCreated,
}: {
  tenant: string
  /** The type whose documents are definitions, as the manifest names it. */
  metaType: string
  open: boolean
  manifest: Manifest | undefined
  onClose: () => void
  /** The created definition, at revision 1. */
  onCreated: (document: Document) => void
}) {
  const [create, { isLoading, error, reset }] = useCreateComponentMutation()
  const [name, setName] = useState('')
  const [role, setRole] = useState('record')

  // The roles come off the definition's own schema, so the select is
  // drawn from the same declaration the validator reads.
  const roles = vocabularyAt(typeOf(manifest, metaType), ['role']).filter((entry) => entry !== '')

  useEffect(() => {
    if (!open) return
    setName('')
    setRole(roles.includes('record') ? 'record' : (roles[0] ?? ''))
    reset()
    // Reset on open, not on mount: the dialog stays mounted between uses.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const slug = slugify(name)
  const valid = slug !== ''

  async function submit() {
    if (!valid) return
    const created = await create({
      tenant,
      createComponent: {
        type: metaType,
        fields: {
          // The prefix is the platform's and is added here so an author
          // types a name rather than a namespaced one. It is what keeps a
          // tenant from shadowing a platform type, and the surface
          // refuses a name without it.
          typeName: `custom/${slug}`,
          // What the author typed, kept as the words rather than
          // discarded once the slug is minted. A type declares its own
          // prose, and this is the one moment somebody has said in
          // plain English what the type is — throwing it away here
          // would mean every client showing `custom/practice` until
          // somebody went back to the designer and typed it again.
          prose: { title: name.trim() },
          role,
          label: ['title'],
          fields: [{ name: 'title', kind: 'text', required: true }],
          intents: [{ name: 'described', sets: ['title'] }],
        },
      },
    }).unwrap()
    onCreated(created)
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Declare a component type</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ pt: 1 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            disabled={isLoading}
            autoFocus
            fullWidth
            helperText={
              slug
                ? `Shown as “${name.trim()}”, stored as custom/${slug}. The stored name is permanent — it is written into every document of this type — and the words are edited freely in the designer.`
                : 'What one of them is called: Practice, Case study, Person.'
            }
          />

          {roles.length > 0 && (
            <TextField
              select
              label="Role"
              value={role}
              onChange={(event) => setRole(event.target.value)}
              disabled={isLoading}
              helperText="What kind of thing this is, and therefore what may hold it. A layout that accepts any record accepts this one."
              sx={{ maxWidth: 320 }}
            >
              {roles.map((entry) => (
                <MenuItem key={entry} value={entry}>
                  {entry}
                </MenuItem>
              ))}
            </TextField>
          )}

          <Alert severity="info">
            It starts with one required <strong>title</strong> field and one intent, <strong>described</strong>, that
            writes it. That is the smallest thing that is actually a type: one with no fields holds nothing, and one
            with no intents can never be edited. Everything else is added in the designer.
          </Alert>

          <Typography variant="caption" color="text.secondary">
            Documents of this type become creatable and placeable as soon as it exists. Changing it later never touches
            what has already been written — a definition evolves forward and migrates nothing.
          </Typography>

          {error && <Alert severity="error">{problemMessage(error, 'The type could not be declared.')}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => submit().catch(shown)} disabled={!valid || isLoading}>
          Declare
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/**
 * What an author typed, as a name the platform accepts.
 *
 * It is done here rather than refused, because the rule — lowercase,
 * digits and single hyphens — is a spelling convention rather than a
 * decision an author is making, and making somebody retype "Case Study"
 * as "case-study" teaches them nothing. The result is shown before it is
 * sent, so nothing is stored that the author did not see.
 */
function slugify(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}
